import { Link } from "react-router-dom";
import { CalendarClock, Hourglass, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, LoadingState, ErrorState, StatusBadge } from "@/components/common";
import { fmtTime, fmtDayLong } from "@/modules/scheduling/shared";
import { useFetch } from "@/lib/useFetch";
import { t } from "@/i18n";

function Row({ a, tz, withDay }) {
  return (
    <li className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 text-sm last:border-0" data-testid={`today-appt-${a.id}`}>
      <span className="min-w-0"><strong>{withDay ? `${fmtDayLong(a.start, tz)} ` : ""}{fmtTime(a.start, tz)}</strong> · {a.service_name} · <span className="text-slate-600">{a.customer_name}</span></span>
      <StatusBadge status={a.status} />
    </li>
  );
}

export function TodayWidgets({ tz }) {
  const { data, error, loading, reload } = useFetch("/garage/today");
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const h = (m) => `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}`;
  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="today-widgets">
      <Card testId="today-appointments-card">
        <div className="mb-3 flex items-center justify-between"><h2 className="font-bold">{t("today.appointments")}</h2><CalendarClock className="h-5 w-5 text-teal-600" /></div>
        {data.appointments.length === 0 ? <p className="text-sm text-slate-500">{t("today.none")}</p> : <ul>{data.appointments.map((a) => <Row key={a.id} a={a} tz={tz} />)}</ul>}
        <Button asChild variant="outline" size="sm" className="mt-4" data-testid="today-open-planning"><Link to="/pro/planning">{t("today.openPlanning")}</Link></Button>
      </Card>
      <Card testId="pending-requests-card">
        <div className="mb-3 flex items-center justify-between"><h2 className="font-bold">{t("today.pending")} <span className="ml-1 rounded-full bg-amber-100 px-2 text-xs text-amber-900" data-testid="pending-count">{data.pending.length}</span></h2><Hourglass className="h-5 w-5 text-amber-600" /></div>
        {data.pending.length === 0 ? <p className="text-sm text-slate-500">{t("today.noPending")}</p> : <ul>{data.pending.slice(0, 6).map((a) => <Row key={a.id} a={a} tz={tz} withDay />)}</ul>}
      </Card>
      <Card testId="workload-card">
        <div className="mb-3 flex items-center justify-between"><h2 className="font-bold">{t("today.workload")}</h2><Gauge className="h-5 w-5 text-teal-600" /></div>
        {data.capacity_minutes === 0 ? <p className="text-sm text-slate-500">{t("today.closed")}</p> : <>
          <p className="font-display text-4xl font-extrabold" data-testid="workload-pct">{data.workload_pct}%</p>
          <Progress value={Math.min(100, data.workload_pct)} className="mt-3 h-2" />
          <p className="mt-3 text-sm text-slate-600" data-testid="free-time">{t("today.free", { free: h(data.free_minutes), total: h(data.capacity_minutes) })}</p>
        </>}
      </Card>
    </div>
  );
}
